import {
  TYPE_CPF,
  TYPE_CNPJ,
  STATES,
  CONTRACT_TYPES,
  COMPANY_INFO_CAME_FROM,
  LOCKABLE_COMPANY_FIELDS
} from "@hub/order/src/constants"
import fillWithZeros from "@shared/helpers/src/fillWithZeros.helper"
import { formatCPF, formatContactType } from "@shared/helpers"
import * as saleService from "@hub/order/src/services/sale.service"
import {
  CONTACT_TYPE,
  PAYMENT_METHOD,
  PERSON_TYPE
} from "@hub/offline-order/src/constants"

export default {
  async fillSale({ commit, dispatch, rootGetters }, sale) {
    const {
      additives,
      customer,
      invoice,
      history,
      seller,
      uuid,
      code,
      date,
      status,
      sub_status,
      services,
      contract_type,
      wallet,
      client_type,
      location_type,
      contract_model,
      sale_origin_uuid,
      request_uuid,
      pos,
      contract_id,
      person_external_id,
      reference_point,
      condominium_id,
      block,
      apartment
    } = sale
    const { address } = customer

    const state = STATES.find(state => state.value === address.state.id)

    const formattedAddress = {
      cep: address?.postcode,
      address: address?.address,
      number: address?.number,
      noNumber: !address?.number,
      neighborhood: address?.neighborhood,
      state: state?.text,
      city: address?.city?.name,
      cityId: address?.city?.uuid,
      stateId: state?.value,
      complement: address?.complement,
      locationType: location_type,
      referencePoint: reference_point
    }

    const formattedCondominiumComplent = {
      condominiumId: condominium_id,
      condominiumName: "",
      block,
      apartment
    }

    formattedAddress.condominiumComplement = formattedCondominiumComplent

    let formattedCustomer = {}

    await dispatch("Order/Contract/fetchContractOptionIfNecessary", null, { root: true })
    await dispatch("Order/Client/fetchCompanyTypesOptions", null, { root: true })

    if (customer.type === "JURIDICA") {
      const { legalPerson } = customer
      let cnpj = legalPerson.cnpj.toString()
      cnpj = fillWithZeros(14, cnpj)
      const companyInfoFromIntegration = legalPerson?.person_legal_origin  === COMPANY_INFO_CAME_FROM.bepeople

      if (legalPerson.opening_date) {
        let OPENING_DATE = legalPerson.opening_date?.split("-")
        let openingDateYear = OPENING_DATE[0]
        let openingDateMonth = OPENING_DATE[1]
        let openingDateDay = OPENING_DATE[2]

        formattedCustomer.openingDate = `${openingDateDay} / ${openingDateMonth} / ${openingDateYear}`
      }

      formattedCustomer.type = TYPE_CNPJ
      formattedCustomer.companyName = legalPerson.social_reason
      formattedCustomer.companyType = legalPerson.person_legal_type
      formattedCustomer.companyState = legalPerson.state_id
      formattedCustomer.cnpj = cnpj
      formattedCustomer.stateRegistration = legalPerson.state_registration
      formattedCustomer.state = legalPerson.state_id
      formattedCustomer.email = legalPerson.email
      formattedCustomer.emailType = legalPerson.email_particularity
      formattedCustomer.cellPhone = legalPerson.cellphone
      formattedCustomer.cellphoneType = legalPerson.cellphone_type
      formattedCustomer.cellphoneParticularity =
        legalPerson.cellphone_particularity
      formattedCustomer.landline = legalPerson.landline
      formattedCustomer.representativeCpf = formatCPF(
        legalPerson.responsible.cpf
      )
      formattedCustomer.representativeName = legalPerson.responsible.name
      formattedCustomer.anotherContact = legalPerson.another_contact
      formattedCustomer.contactType = formatContactType(legalPerson?.contact_type)
      formattedCustomer.whatsappContact = legalPerson.whatsapp,
      formattedCustomer.legalResponsibles = [{
        name: legalPerson.responsible.name,
        cpf: String(legalPerson.responsible.cpf),
        qualified: true,
        fromIntegration: false
      }]
      formattedCustomer.isCompanyTypeFromBepeople = companyInfoFromIntegration && legalPerson?.locked_fields?.includes(LOCKABLE_COMPANY_FIELDS.companyType)
      formattedCustomer.isCompanyStateFromBepeople = companyInfoFromIntegration && legalPerson?.locked_fields?.includes(LOCKABLE_COMPANY_FIELDS.companyState)
    }

    if (customer.type === "FISICA") {
      const { physicalPerson } = customer
      const isSchooled =
        physicalPerson.schooled === null
          ? null
          : Boolean(physicalPerson.schooled)

      formattedCustomer.type = TYPE_CPF
      formattedCustomer.cpf = fillWithZeros(11, physicalPerson.cpf.toString())
      formattedCustomer.rg = physicalPerson.rg
      formattedCustomer.generalRegistrationExempt =
        physicalPerson.general_registration_exempt
      formattedCustomer.fullName = physicalPerson.name
      formattedCustomer.birthDate = physicalPerson.birthdate
        .split("-")
        .reverse()
        .join("/")
      formattedCustomer.motherName = physicalPerson.mother_name
      formattedCustomer.fatherName = physicalPerson.father_name
      formattedCustomer.gender = physicalPerson.gender
      formattedCustomer.maritalStatus = physicalPerson.marital_status
      formattedCustomer.profession = physicalPerson.profession
      formattedCustomer.email = physicalPerson.email
      formattedCustomer.emailType = physicalPerson.email_particularity
      formattedCustomer.cellPhone = physicalPerson.cellphone
      formattedCustomer.cellphoneType = physicalPerson.cellphone_type
      formattedCustomer.cellphoneParticularity =
        physicalPerson.cellphone_particularity
      formattedCustomer.landline = physicalPerson.landline
      formattedCustomer.anotherContact = physicalPerson.another_contact
      formattedCustomer.schooled = isSchooled,
      formattedCustomer.contactType = formatContactType(physicalPerson?.contact_type),
      formattedCustomer.whatsappContact = physicalPerson.whatsapp
    }

    let formattedContract = {}

    if (additives) {
      formattedContract = {
        additives: additives.map(adt => ({
          text: adt.name,
          value: adt.uuid
        })),
        contract_type: contract_type
      }

      if (sale.accession) {
        formattedContract.accession = sale.accession
      }
    }

    let formattedBilling = {}

    if (invoice) {
      let shipping =
        rootGetters["Order/Contract/getShippingMethodsOptions"]?.find(
          option => option.searchable === invoice.shipping_method
        )?.slug

      let payment =
        rootGetters["Order/Contract/getPaymentTypesOptions"]?.find(
          option => option.searchable === invoice.payment_type
        )?.slug

      if (payment === "DEBITO_AUTOMATICO") {
        formattedBilling.bank = invoice.account_info.bank_id
        formattedBilling.agency = invoice.account_info.agency
        formattedBilling.account = invoice.account_info.account
        formattedContract.bank = invoice.account_info.bank_id
        formattedContract.agency = invoice.account_info.agency
        formattedContract.account = invoice.account_info.account
      }

      formattedBilling.shipping = shipping
      formattedBilling.dueDate = invoice.due_day
      formattedBilling.payment = payment
      formattedContract.shipping = shipping
      formattedContract.dueDate = invoice.due_day
      formattedContract.payment = payment
      formattedBilling.clientType = client_type
      formattedBilling.contractType = contract_type
      formattedBilling.contractModel = contract_model
      formattedBilling.whatsapp = invoice.whatsapp
      formattedBilling.uuid = uuid

      formattedContract.contractId = contract_id,
      formattedContract.personalExternalId = person_external_id
    }

    if (customer.charge_address) {
      formattedBilling.billingAddress = {
        isSameAddress: false,
        cep: customer.charge_address.postcode,
        address: customer.charge_address.address,
        neighborhood: customer.charge_address.neighborhood,
        city: customer.charge_address.city.name,
        cityId: customer.charge_address.city.uuid,
        state: customer.charge_address.state.name,
        stateId: customer.charge_address.state.id,
        number: customer.charge_address.number,
        noNumber: !customer.charge_address.number
      }
      formattedBilling.complements = customer?.charge_address?.complement
    }

    function formatDependent(dependent, type) {
      return {
        id: dependent.uuid,
        badge: dependent.badge,
        type,
        movement: dependent.movement_name,
        portability: dependent.portability,
        currentProvider: dependent.carrier,
        lineNumber: dependent.current_line,
        iccid: dependent.iccid
      }
    }

    const formattedServices = services.map(service => {
      const paidCount = service.dependents?.paid_count ?? 0
      const dependentUnitValue = Number(service.dependents?.value ?? 0)

      return {
        ...service,
        offer_type: service.entity,
        category: {
          description: service.type
        },
        isSavedService: true,
        value: service.amount,

        total_price: Number(service.amount) + (paidCount * dependentUnitValue),

        dependents: [
          ...(service.dependents?.free ?? []).map(dependent =>
            formatDependent(dependent, "free")
          ),
          ...(service.dependents?.paid ?? []).map(dependent =>
            formatDependent(dependent, "paid")
          )
        ]
      }
    })

    dispatch("Order/Client/fillClient", formattedCustomer, { root: true })
    dispatch("Order/Address/fillAddress", formattedAddress, { root: true })
    commit("Order/Offer/setOffersSelecteds", formattedServices, { root: true })
    commit("Order/POS/updateCaptation", sale_origin_uuid, { root: true })
    commit("Order/POS/updateWallet", wallet, { root: true })
    commit("Order/POS/setPosCnpj", pos?.cnpj, { root: true })
    commit("Order/Core/setSaleItemUuid", sale.services[0].service_uuid, {
      root: true
    })
    commit("Order/Core/setSaleHistory", history, { root: true })
    commit("Order/Core/setSaleSeller", seller, { root: true })
    commit("Order/Core/setSaleUuid", uuid, { root: true })
    commit("Order/Core/setSaleId", request_uuid, { root: true })
    commit("Order/Core/setSaleCode", code, { root: true })
    commit("Order/Core/setSaleDate", date, { root: true })
    commit("Order/Core/setSaleStatus", status, { root: true })
    commit("Order/Core/setSaleSubStatus", sub_status, { root: true })
    dispatch("Order/Billing/fillBilling", formattedBilling, { root: true })
    dispatch("Order/Contract/fillContract", formattedContract, { root: true })
  },

  returnSaleProperties({ rootGetters }) {
    const data = {
      plans:
        rootGetters["Order/Offer/getOffers"]?.map(offer => offer.uuid) || null
    }
    data.payment_info = {
      due_day: rootGetters["Order/Contract/getDueDate"] || null,
      payment_type:
        rootGetters["Order/Contract/getPaymentTypesOptions"]
          ?.find(({ searchable }) =>
            searchable === rootGetters["Order/Contract/getPayment"]
          )?.slug || null,
      shipping_method:
        rootGetters["Order/Contract/getShippingMethodsOptions"]
          ?.find(({ value }) =>
            value == rootGetters["Order/Contract/getShipping"]
          )?.slug || null,
    }

    if (rootGetters["Order/Contract/getContractType"]) {
      data.contract_type = CONTRACT_TYPES.find(
        types =>
          types.value == rootGetters["Order/Contract/getContractType"]
      )?.value || null
    }

    if (rootGetters["Order/Contract/getAdditives"]?.length) {
      data.additives = rootGetters["Order/Contract/getAdditives"].map(
        additivies => additivies.value
      )
    }
    if (rootGetters["Order/Billing/getProviderAddressUuid"]) {
      data.charge_address = {
        postcode: rootGetters["Order/Billing/getBillingAddress"].cep,
        address: rootGetters["Order/Billing/getBillingAddress"].address,
        number: rootGetters["Order/Billing/getBillingAddress"].number,
        neighborhood:
          rootGetters["Order/Billing/getBillingAddress"].neighborhood,
        city_uuid: rootGetters["Order/Billing/getBillingAddress"].cityId,
        complement: rootGetters["Order/Billing/getComplements"]?.length
          ? rootGetters["Order/Billing/getComplements"]
          : null
      }
    }
    return data
  },

  async saveSaleProperties({ rootGetters, commit, dispatch }) {
    const data = await dispatch("returnSaleProperties")
    saleService.saveSaleProperties({
      id: rootGetters["Order/Core/saleId"],
      data
    })
  },

  fillTransformSale({ commit, dispatch }, sale) {
    const { address, payment_info, person } = sale

    const formattedCustomer = formatCustomer(person)
    dispatch("Order/Client/fillClient", formattedCustomer, { root: true })

    const formattedAddress = formatAddress(address)
    dispatch("Order/Address/fillAddress", formattedAddress, { root: true })

    const formattedContract = formatContract(payment_info)
    const formattedBilling = formatBilling(payment_info)

    dispatch("Order/Billing/fillBilling", formattedBilling, { root: true })
    dispatch("Order/Contract/fillContract", formattedContract, { root: true })

    commit("setDataToTransform", sale)
  }
}

function formatCustomer(person) {
  const baseCustomer = {
    email: getContactValue(person.contacts, CONTACT_TYPE.EMAIL),
    cellPhone: getContactValue(person.contacts, CONTACT_TYPE.CELLPHONE),
    landline: getContactValue(person.contacts, CONTACT_TYPE.LANDLINE),
    anotherContact: getContactValue(
      person.contacts,
      CONTACT_TYPE.ANOTHER_CONTACT
    )
  }

  if (person.person_type === PERSON_TYPE.LEGAL) {
    return {
      ...baseCustomer,
      type: TYPE_CNPJ,
      companyName: person.social_reason,
      companyType: person.legal_type,
      cnpj: fillWithZeros(14, person.document.toString()),
      stateRegistration: person.state_registration,
      representativeCpf: formatCPF(person.responsible?.cpf),
      representativeName: person.responsible?.name,
      openingDate: formatOpeningDate(person.opening_date)
    }
  }

  if (person.person_type === PERSON_TYPE.INDIVIDUAL) {
    return {
      ...baseCustomer,
      type: TYPE_CPF,
      cpf: fillWithZeros(11, person.document.toString()),
      rg: person.general_registration,
      fullName: person.name,
      birthDate: formatDate(person.birthdate),
      motherName: person.mother_name,
      fatherName: person.father_name,
      gender: person.gender,
      maritalStatus: person.marital_status,
      profession: person.profession,
      schooled: person.schooled
    }
  }

  return {}
}

function getContactValue(contacts, type) {
  return contacts.find(contact => contact.type === type)?.value || null
}

function formatOpeningDate(date) {
  if (!date) return null
  const [year, month, day] = date.split("-")
  return `${day} / ${month} / ${year}`
}

function formatDate(date) {
  return date ? date.split("-").reverse().join("/") : null
}

function formatAddress(address) {
  return {
    number: address.number,
    noNumber: !address.number
  }
}

function formatContract(payment_info) {
  const contract = { additives: [] }
  if (payment_info.payment_type === PAYMENT_METHOD.DEBIT) {
    contract.agency = payment_info?.automatic_debit?.agency
    contract.account = payment_info?.automatic_debit?.account
  }
  return contract
}

function formatBilling(payment_info) {
  if (!payment_info.charge_address) return {}
  const { address } = payment_info
  return {
    billingAddress: {
      isSameAddress: false,
      cep: address.postcode,
      address: address.address,
      neighborhood: address.neighborhood,
      city: address.city.name,
      cityId: address.city.uuid,
      state: address.state,
      stateId: address.state,
      number: address.number,
      noNumber: !address.number
    }
  }
}
